// src/pages/District.jsx
import React from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { normalizeMediaUrl } from '../api/base';
import DistrictSlider from '../components/DistrictSlider';
import DistrictManagers from '../components/DistrictManagers';
import DistrictPropertiesSection from '../components/DistrictPropertiesSection';
import DistrictDescription from '../components/DistrictDescription';
import YMap from '../components/YMap';
import { useCurrencyStore } from '../store/useCurrencyStore';

const Wrap = styled.main`
  width: min(1400px, 96vw);
  margin: 20px auto;
  padding: 0 18px;
`;

const IntroSection = styled.section`
  display: flex;
  gap: 40px;
  margin: 32px 0;
  align-items: stretch; /* Растягиваем элементы по высоте */
  
  @media (max-width: 980px) {
    flex-direction: column;
    gap: 32px;
    align-items: flex-start;
  }
`;

const IntroContent = styled.div`
  flex: 2; /* 2/3 ширины */
  display: flex;
  flex-direction: column;
  justify-content: space-between; /* Распределяем содержимое по высоте */
  min-height: 100%; /* Минимальная высота 100% */
  
  .content-wrapper {
    flex-grow: 1; /* Растягиваем обертку контента */
    display: flex;
    flex-direction: column;
  }
  
  .title {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 20px;
    line-height: 1.2;
    color: var(--fg);
    
    @media (max-width: 768px) {
      font-size: 26px;
    }
  }
  
  .short-description {
    font-size: 18px;
    line-height: 1.8;
    color: var(--fg);
    margin-bottom: 28px;
    text-align: justify;
    max-width: 100%;
    flex-grow: 1; /* Растягиваем описание */
    
    /* Улучшенная типографика */
    font-weight: 400;
    letter-spacing: 0.01em;
    
    /* Форматирование абзацев */
    p {
      margin-bottom: 16px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    /* Если текст содержит переносы строк */
    white-space: pre-line;
    
    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 1.7;
      margin-bottom: 24px;
    }
  }
  
  .more-button {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: var(--primary);
    text-decoration: none;
    font-weight: 600;
    padding: 14px 24px;
    border: 2px solid var(--primary);
    border-radius: 12px;
    transition: all 0.3s ease;
    font-size: 15px;
    background: transparent;
    align-self: flex-start; /* Выравниваем кнопку по левому краю */
    margin-top: auto; /* Прижимаем кнопку к низу */
    
    &:hover {
      background: var(--primary);
      color: var(--primaryFg);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(176, 141, 87, 0.4);
    }
    
    svg {
      transition: transform 0.3s ease;
      width: 18px;
      height: 18px;
    }
    
    &:hover svg {
      transform: translateY(3px);
    }
    
    @media (max-width: 768px) {
      padding: 12px 20px;
      font-size: 14px;
    }
  }
`;

const ManagersSection = styled.div`
  flex: 1; /* 1/3 ширины */
  position: sticky;
  top: 20px;
  align-self: flex-start; /* Выравниваем по верху */
  
  h3 {
    margin-bottom: 20px;
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    color: var(--fg);
    
    @media (max-width: 980px) {
      text-align: left;
      margin-top: 0;
      font-size: 18px;
    }
  }
  
  @media (max-width: 980px) {
    position: static;
    flex: none;
    align-self: stretch;
  }
`;

const sanitizeHtml = (html = '') => html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');

const pickImages = (d) => {
  const list = Array.isArray(d?.images) ? d.images
            : Array.isArray(d?.photos) ? d.photos
            : Array.isArray(d?.gallery) ? d.gallery
            : [];
  return list
    .map((it) => {
      if (typeof it === 'string') {
        return {
          url: normalizeMediaUrl(it),
          title: '',
          description: ''
        };
      }
      if (it && typeof it === 'object') {
        // Приоритет: url (оригинал) > thumbnail_url (превью)
        const imageUrl = it.url || it.thumbnail_url;
        return imageUrl ? {
          url: normalizeMediaUrl(imageUrl),
          title: it.title || it.alt_text || '',
          description: it.description || it.caption || ''
        } : null;
      }
      return null;
    })
    .filter(Boolean);
};

export default function District() {
  const { id: slug } = useParams();
  const { i18n } = useTranslation();
  const currencyCode = useCurrencyStore(s => s.currency) || 'AED';

  const [district, setDistrict] = React.useState(null);
  const [managers, setManagers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');

  // Состояния для разных типов недвижимости
  const [primaryProps, setPrimaryProps] = React.useState([]);
  const [secondaryProps, setSecondaryProps] = React.useState([]);
  const [commercialProps, setCommercialProps] = React.useState([]);
  const [rentProps, setRentProps] = React.useState([]);
  const [commercialRentProps, setCommercialRentProps] = React.useState([]);

  // Состояния пагинации для каждого типа
  const [primaryPagination, setPrimaryPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [secondaryPagination, setSecondaryPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [commercialPagination, setCommercialPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [rentPagination, setRentPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [commercialRentPagination, setCommercialRentPagination] = React.useState({ page: 1, pages: 1, total: 0 });

  const [loadingStates, setLoadingStates] = React.useState({
    primary: true,
    secondary: true,
    commercial: true,
    rent: true,
    commercialRent: true
  });

  // Загрузка информации о районе
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const d = await PublicAPI.district(slug, { locale: i18n.language || 'ru' });
        if (!cancelled) {
          setDistrict(d?.district || d);
          
            // Тестовые менеджеры для района
          setManagers([
            {
              id: 1,
              first_name: 'Анна',
              last_name: 'Иванова',
              full_name: 'Анна Иванова',
              position_ru: 'Эксперт по району',
              position_en: 'District Expert',
              phone: '+971 52 342-36-86',
              email: 'anna.ivanova@astorius.ae',
              telegram: 'https://t.me/anna_ivanova_astorius',
              avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop&crop=face'
            },
            {
              id: 2,
              first_name: 'Михаил',
              last_name: 'Петров',
              full_name: 'Михаил Петров',
              position_ru: 'Менеджер по продажам',
              position_en: 'Sales Manager',
              phone: '+971 52 342-36-87',
              email: 'mikhail.petrov@astorius.ae',
              telegram: 'https://t.me/mikhail_petrov_astorius',
              avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face'
            },
            {
              id: 3,
              first_name: 'Елена',
              last_name: 'Смирнова',
              full_name: 'Елена Смирнова',
              position_ru: 'Консультант по инвестициям',
              position_en: 'Investment Consultant',
              phone: '+971 52 342-36-88',
              email: 'elena.smirnova@astorius.ae',
              telegram: 'https://t.me/elena_smirnova_astorius',
              avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face'
            },
            {
              id: 4,
              first_name: 'Александр',
              last_name: 'Козлов',
              full_name: 'Александр Козлов',
              position_ru: 'Специалист по аренде',
              position_en: 'Rental Specialist',
              phone: '+971 52 342-36-89',
              email: 'alexander.kozlov@astorius.ae',
              telegram: 'https://t.me/alexander_kozlov_astorius',
              avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face'
            }
          ]);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || 'Error');
          setDistrict(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, i18n.language]);

  // Загрузка недвижимости по типам с пагинацией
  const loadPropertiesByType = React.useCallback(async (dealType, propertyType = null, page = 1, setProps, setPagination, setLoading) => {
    setLoading(true);
    try {
      const res = await PublicAPI.districtProps(slug, {
        deal_type_id: dealType,
        property_type_id: propertyType,
        page: page,
        page_size: 4, // 2 ряда по 2 объекта
        locale: i18n.language || 'ru',
        currency: currencyCode || 'AED'
      });

      const props = Array.isArray(res?.properties) ? res.properties
                  : Array.isArray(res?.items) ? res.items
                  : [];
      
      const pagination = res?.pagination || {};
      
      setProps(props);
      setPagination({
        page: Number(pagination.page || page),
        pages: Number(pagination.pages || Math.ceil((pagination.total || props.length) / 4)),
        total: Number(pagination.total || props.length)
      });
    } catch (e) {
      console.error(`Error loading properties for deal type ${dealType}:`, e);
      setProps([]);
      setPagination({ page: 1, pages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [slug, i18n.language]);

  // Специальная функция для загрузки вторичной недвижимости
  const loadSecondaryProperties = React.useCallback(async (page = 1, setProps, setPagination, setLoading) => {
    setLoading(true);
    try {
      const res = await PublicAPI.districtProps(slug, {
        deal_type_id: 2, // ID для вторичной недвижимости
        page: page,
        page_size: 4, // 2 ряда по 2 объекта
        locale: i18n.language || 'ru',
        currency: currencyCode || 'AED'
      });

      const props = Array.isArray(res?.properties) ? res.properties
                  : Array.isArray(res?.items) ? res.items
                  : [];
      
      const pagination = res?.pagination || {};
      
      setProps(props);
      setPagination({
        page: Number(pagination.page || page),
        pages: Number(pagination.pages || Math.ceil((pagination.total || props.length) / 4)),
        total: Number(pagination.total || props.length)
      });
    } catch (e) {
      console.error('Error loading secondary properties:', e);
      setProps([]);
      setPagination({ page: 1, pages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [slug, i18n.language]);

  // Функции для изменения страниц
  const handlePrimaryPageChange = React.useCallback((page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadPropertiesByType(1, null, page, setPrimaryProps, setPrimaryPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, primary: loading }))
    );
  }, [loadPropertiesByType]);

  const handleSecondaryPageChange = React.useCallback((page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadSecondaryProperties(page, setSecondaryProps, setSecondaryPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, secondary: loading }))
    );
  }, [loadSecondaryProperties]);

  const handleCommercialPageChange = React.useCallback((page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadPropertiesByType(1, 4, page, setCommercialProps, setCommercialPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, commercial: loading }))
    );
  }, [loadPropertiesByType]);

  const handleRentPageChange = React.useCallback((page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadPropertiesByType(4, null, page, setRentProps, setRentPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, rent: loading }))
    );
  }, [loadPropertiesByType]);

  const handleCommercialRentPageChange = React.useCallback((page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadPropertiesByType(4, 4, page, setCommercialRentProps, setCommercialRentPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, commercialRent: loading }))
    );
  }, [loadPropertiesByType]);

  // Первоначальная загрузка всех типов недвижимости
  React.useEffect(() => {
    if (!slug) return;

    // Загружаем первую страницу для всех типов
    loadPropertiesByType(1, null, 1, setPrimaryProps, setPrimaryPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, primary: loading }))
    );
    
    loadSecondaryProperties(1, setSecondaryProps, setSecondaryPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, secondary: loading }))
    );
    
    loadPropertiesByType(1, 4, 1, setCommercialProps, setCommercialPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, commercial: loading }))
    );
    
    loadPropertiesByType(4, null, 1, setRentProps, setRentPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, rent: loading }))
    );
    
    loadPropertiesByType(4, 4, 1, setCommercialRentProps, setCommercialRentPagination, (loading) => 
      setLoadingStates(prev => ({ ...prev, commercialRent: loading }))
    );
  }, [slug, loadPropertiesByType, loadSecondaryProperties]);

  const images = pickImages(district);
  
  // Собираем все объекты для карты
  const allProperties = React.useMemo(() => {
    return [...primaryProps, ...secondaryProps, ...commercialProps, ...rentProps, ...commercialRentProps];
  }, [primaryProps, secondaryProps, commercialProps, rentProps, commercialRentProps]);

  // Мемоизируем markers для карты
  const markers = React.useMemo(() => {
    return allProperties
      .map(it => {
        const lat = Number(it.lat);
        const lng = Number(it.lng);
        return {
          coords: [lat, lng],
          title: it.title || it.name,
          slug: it.slug,
          id: it.id || it.slug,
          price: it.price,
          subtitle: it.address || ''
        };
      })
      .filter(m => Number.isFinite(m.coords[0]) && Number.isFinite(m.coords[1]));
  }, [allProperties]);

  const mapCenter = React.useMemo(() => {
    const lat = Number(allProperties?.[0]?.lat);
    const lng = Number(allProperties?.[0]?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return [55.751244, 37.618423];
  }, [allProperties]);
  
  // Улучшенное получение краткого описания
  const shortDescription = React.useMemo(() => {
    if (district?.short_description) {
      return district.short_description;
    }
    
    if (district?.excerpt) {
      return district.excerpt;
    }
    
    if (district?.description) {
      // Очищаем HTML теги и получаем полный текст
      const cleanText = district.description
        .replace(/<[^>]*>/g, '') // Убираем HTML теги
        .replace(/\s+/g, ' ') // Заменяем множественные пробелы на одинарные
        .trim();
      
      // Разбиваем на абзацы и предложения
      const paragraphs = cleanText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
      const sentences = cleanText.split(/[.!?]+/).filter(s => s.trim().length > 20);
      
      let shortText = '';
      
      // Если есть абзацы, берем первые 2-3 абзаца (оптимально для высоты менеджеров)
      if (paragraphs.length > 1) {
        shortText = paragraphs.slice(0, Math.min(3, paragraphs.length)).join('\n\n');
      } else {
        // Если нет абзацев, берем 6-8 предложений
        shortText = sentences.slice(0, Math.min(8, sentences.length)).join('. ') + '.';
      }
      
      // Если текста мало, добираем немного из полного описания
      if (shortText.length < 500) {
        const additionalText = cleanText.substring(shortText.length, 700);
        const lastSentence = additionalText.lastIndexOf('.');
        if (lastSentence > 50) {
          shortText += ' ' + additionalText.substring(0, lastSentence + 1);
        }
      }
      
      return shortText;
    }
    
    // Оптимальное дефолтное описание для соответствия высоте менеджеров
    const districtName = String(district?.name || 'Район');
    return `${districtName} — это престижный район с развитой инфраструктурой и удобным расположением в самом сердце города. Здесь гармонично сочетаются современная архитектура, зеленые зоны и близость к основным достопримечательностям.

Район отличается высоким уровнем комфорта и безопасности, предлагая жителям и инвесторам уникальные возможности для жизни и бизнеса. Развитая транспортная сеть обеспечивает удобное сообщение с другими частями города.

Инфраструктура района включает в себя торговые центры мирового класса, рестораны высокой кухни, образовательные учреждения и современные медицинские центры. Это делает ${districtName} идеальным местом как для постоянного проживания, так и для инвестиций в недвижимость.

Близость к деловому центру, международному аэропорту и основным магистралям города делает этот район особенно привлекательным для успешных людей и международных инвесторов. Высокий потенциал роста стоимости недвижимости обеспечивает отличные перспективы для капиталовложений.

Экологически чистая среда, современные системы безопасности и круглосуточная охрана создают комфортные условия для семейной жизни. Развитая социальная инфраструктура включает детские сады, школы и парки для отдыха.`;
  }, [district]);

  if (loading) {
    return (
      <Wrap>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
          {i18n.language === 'en' ? 'Loading...' : 'Загрузка...'}
        </div>
      </Wrap>
    );
  }

  if (!district) {
    return (
      <Wrap>
        <h2>{i18n.language === 'en' ? 'District not found' : 'Район не найден'}</h2>
        {err && <p style={{ color: '#c62828' }}>{err}</p>}
        <Link to="/districts" className="btn">
          ← {i18n.language === 'en' ? 'Back to districts' : 'Вернуться к районам'}
        </Link>
      </Wrap>
    );
  }

  return (
    <Wrap>
      {/* Слайдер района */}
      <DistrictSlider district={district} images={images} />

      {/* Краткое описание и менеджеры */}
      <IntroSection>
        <IntroContent>
          <div className="content-wrapper">
            <h1 className="title">{String(district.name || slug)}</h1>
            
            {shortDescription && (
              <div className="short-description">
                {shortDescription}
              </div>
            )}
          </div>
          
          <a href="#description" className="more-button">
            {i18n.language === 'en' ? 'Read more' : 'Подробнее'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
            </svg>
          </a>
        </IntroContent>

        <ManagersSection>
          <h3>{i18n.language === 'en' ? 'District Experts' : 'Эксперты по району'}</h3>
          <DistrictManagers managers={managers} language={i18n.language} />
        </ManagersSection>
      </IntroSection>

      {/* Карта района с объектами */}
      {markers.length > 0 && (
        <YMap
          apiKey={process.env.REACT_APP_YMAPS_KEY}
          lang={i18n.language === 'en' ? 'en_US' : 'ru_RU'}
          center={mapCenter}
          zoom={14}
          markers={markers}
          isLink={true}
        />
      )}

      {/* Секции недвижимости с пагинацией */}
      <DistrictPropertiesSection
        title={i18n.language === 'en' ? 'New Buildings' : 'Новостройки'}
        properties={primaryProps}
        loading={loadingStates.primary}
        pagination={primaryPagination}
        onPageChange={handlePrimaryPageChange}
        viewAllLink={`/search?district=${slug}&deal_type=1`}
        language={i18n.language}
        emptyMessage={i18n.language === 'en' ? 'No new buildings in this district' : 'В этом районе нет новостроек'}
      />

      <DistrictPropertiesSection
        title={i18n.language === 'en' ? 'Secondary Market' : 'Вторичная недвижимость'}
        properties={secondaryProps}
        loading={loadingStates.secondary}
        pagination={secondaryPagination}
        onPageChange={handleSecondaryPageChange}
        viewAllLink={`/secondary?district=${slug}`}
        language={i18n.language}
        emptyMessage={i18n.language === 'en' ? 'No secondary properties in this district' : 'В этом районе нет вторичной недвижимости'}
      />

      <DistrictPropertiesSection
        title={i18n.language === 'en' ? 'Commercial Properties' : 'Коммерческая недвижимость'}
        properties={commercialProps}
        loading={loadingStates.commercial}
        pagination={commercialPagination}
        onPageChange={handleCommercialPageChange}
        viewAllLink={`/commercial?district=${slug}`}
        language={i18n.language}
        emptyMessage={i18n.language === 'en' ? 'No commercial properties in this district' : 'В этом районе нет коммерческой недвижимости'}
      />

      <DistrictPropertiesSection
        title={i18n.language === 'en' ? 'Rental Properties' : 'Аренда недвижимости'}
        properties={rentProps}
        loading={loadingStates.rent}
        pagination={rentPagination}
        onPageChange={handleRentPageChange}
        viewAllLink={`/rent?district=${slug}`}
        language={i18n.language}
        emptyMessage={i18n.language === 'en' ? 'No rental properties in this district' : 'В этом районе нет объектов для аренды'}
      />

      <DistrictPropertiesSection
        title={i18n.language === 'en' ? 'Commercial Rental' : 'Коммерческая аренда'}
        properties={commercialRentProps}
        loading={loadingStates.commercialRent}
        pagination={commercialRentPagination}
        onPageChange={handleCommercialRentPageChange}
        viewAllLink={`/commercial-rent?district=${slug}`}
        language={i18n.language}
        emptyMessage={i18n.language === 'en' ? 'No commercial rental properties in this district' : 'В этом районе нет коммерческих объектов для аренды'}
      />

      {/* Подробное описание района */}
      <DistrictDescription 
        district={district} 
        language={i18n.language}
        categories={{
          restaurants: [
            { name: 'Nobu Dubai', description: 'Японская кухня премиум-класса', distance: '0.5 км' },
            { name: 'Zuma', description: 'Современная японская кухня', distance: '0.8 км' },
            { name: 'La Petite Maison', description: 'Французская кухня', distance: '1.2 км' }
          ],
          shopping: [
            { name: 'Dubai Mall', description: 'Крупнейший торговый центр', distance: '2.1 км' },
            { name: 'Mall of the Emirates', description: 'Премиальный шопинг', distance: '3.5 км' }
          ],
          culture: [
            { name: 'Dubai Opera', description: 'Главная оперная площадка', distance: '1.5 км' },
            { name: 'Dubai Museum', description: 'Музей истории Дубая', distance: '2.3 км' }
          ],
          entertainment: [
            { name: 'Burj Khalifa', description: 'Самое высокое здание в мире', distance: '2.0 км' },
            { name: 'Dubai Fountain', description: 'Музыкальные фонтаны', distance: '2.1 км' }
          ]
        }}
      />

      {err && <div style={{ color: '#c62828', marginTop: 20 }}>{err}</div>}
    </Wrap>
  );
}
